import { styled } from '@mui/material/styles';
import RadioGroup, { useRadioGroup } from '@mui/material/RadioGroup';
import FormControlLabel, { FormControlLabelProps } from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import { FormControl, FormHelperText, FormLabel, IconButton, Tooltip } from '@mui/material';
import { FormikContextType, FormikValues } from 'formik';
import { ChangeEvent, useContext } from 'react';
import { FormikContext } from 'formik';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CloseIcon from '@mui/icons-material/Close';
import CancelIcon from '@mui/icons-material/Cancel';
import RefreshIcon from '@mui/icons-material/Refresh';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

interface StyledFormControlLabelProps extends FormControlLabelProps {
  checked: boolean;
}

const StyledFormControlLabel = styled((props: StyledFormControlLabelProps) => (
  <FormControlLabel {...props} />
))(({ theme, checked }) => ({
  paddingRight: '10px',
  borderRadius: '12px',
  backgroundColor: 'rgba(240, 242, 245, 0.8)',
  border: '1px solid transparent',
  color: 'rgba(38, 48, 60, 0.6)',
  marginLeft: 0,
  marginBottom: '10px',
  '&': checked && {
    backgroundColor: 'rgba(0, 87, 155, 0.0535)',
    border: '1px solid #00579B',
  },
  '.MuiFormControlLabel-label': {
    fontWeight: 500,
    fontSize: '14px',
    '&': checked && {
      color: theme.palette.primary.main,
    },
  },
}));

function MyFormControlLabel(props: FormControlLabelProps) {
  const radioGroup = useRadioGroup();

  let checked = false;

  if (radioGroup) {
    checked = radioGroup.value === props.value;
  }

  return <StyledFormControlLabel checked={checked} {...props} />;
}

export default function UseRadioGroup({
  name,
  formLabel,
  defaultValue,
  items,
  labelColor = 'rgba(38, 48, 60, 0.7)',
  value,
  disabled = false,
  onChange,
  enableUnselect = false,
  info,
}: {
  name: string;
  formLabel?: string;
  defaultValue?: string | boolean;
  labelColor?: string;
  items: { label: string; value: string | boolean | number; disabled?: boolean }[];
  value?: boolean | string | number;
  disabled?: boolean;
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  enableUnselect?: boolean;
  info?: string;
}): JSX.Element {
  const formik = useContext(FormikContext) as FormikContextType<FormikValues> | undefined;

  const field = formik ? formik.getFieldProps(name) : undefined;
  const meta = formik ? formik.getFieldMeta(name) : undefined;
  const errorText = meta?.error && meta.touched ? meta.error : '';

  const fieldValue = value !== undefined ? value : field?.value ?? '';
  const handleChange = onChange || field?.onChange;
  const clearValue = () => {
    if (disabled) return;
    if (formik) {
      formik.setFieldValue(name, '');
      return;
    }
    handleChange?.({ target: { name, value: '' } } as ChangeEvent<HTMLInputElement>);
  };

  return (
    <FormControl component="fieldset" sx={{ width: '100%' }}>
      {formLabel && (
        <FormLabel
          component="legend"
          sx={{
            mb: 2,
            fontWeight: 500,
            fontSize: 14,
            color: labelColor,
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
          }}>
          {formLabel}
          {enableUnselect && (
            <IconButton
              sx={{
                p: 0,
                pr: 0.5,
                ':hover': {
                  bgcolor: '#F4FCFC',
                },
                '&.Mui-disabled': {
                  cursor: !disabled ? 'pointer' : 'not-allowed !important',
                  pointerEvents: 'all',
                },
              }}
              onClick={clearValue}
              disabled={disabled}>
              <RefreshIcon color={!disabled ? 'primary' : 'disabled'} fontSize="small" />
            </IconButton>
          )}
          {info && (
            <Tooltip title={info}>
              <InfoOutlinedIcon />
            </Tooltip>
          )}
        </FormLabel>
      )}
      <RadioGroup
        row
        defaultValue={defaultValue}
        {...(field || {})}
        onChange={handleChange}
        value={fieldValue}
        sx={{
          '& .Mui-disabled': {
            cursor: !disabled ? 'pointer' : 'not-allowed !important',
            pointerEvents: 'all',
          },
        }}>
        {items.map((item, index) => (
          <MyFormControlLabel
            key={index}
            label={item.label}
            value={String(item.value)}
            disabled={disabled || item.disabled}
            control={<Radio disableRipple size="small" sx={{ ':hover': { background: 'none' } }} />}
          />
        ))}
      </RadioGroup>
      {errorText && (
        <FormHelperText error sx={{ marginLeft: 'unset' }}>
          {errorText}
        </FormHelperText>
      )}
    </FormControl>
  );
}
